import { useId, type ReactNode } from "react";
import type { Token } from "../lib/tokens";

/**
 * Token badge, inline SVG only, never an external image. A token whose country
 * is unambiguous wears its flag; any other keeps the colour and glyph circle.
 */

/** Flag artwork, drawn on a 24×24 canvas and clipped to the circle. */
type FlagId = "us" | "eu" | "tr" | "br" | "id" | "ca";

/** Symbols whose issuing jurisdiction is unambiguous. */
const FLAG_BY_SYMBOL: Readonly<Record<string, FlagId>> = {
  USD: "us",
  USDC: "us",
  EUR: "eu",
  EURC: "eu",
  TRY: "tr",
  TRYB: "tr",
  BRL: "br",
  BRZ: "br",
  BBRL: "br",
  IDR: "id",
  IDRX: "id",
  CAD: "ca",
  CADC: "ca",
};

/** Star for the EU ring / the Turkish flag, as a unit path scaled by the caller. */
function star(cx: number, cy: number, r: number, fill: string, key?: string): ReactNode {
  const points: string[] = [];
  for (let i = 0; i < 10; i += 1) {
    const radius = i % 2 === 0 ? r : r * 0.42;
    const angle = (Math.PI / 5) * i - Math.PI / 2;
    points.push(`${(cx + radius * Math.cos(angle)).toFixed(2)},${(cy + radius * Math.sin(angle)).toFixed(2)}`);
  }
  return <polygon key={key} points={points.join(" ")} fill={fill} />;
}

function UsFlag() {
  // 13 stripes compressed to 7 bands: at 24px the real count is a grey smear.
  const band = 24 / 7;
  const stripes: ReactNode[] = [];
  for (let i = 0; i < 7; i += 1) {
    stripes.push(
      <rect
        key={i}
        x="0"
        y={i * band}
        width="24"
        height={band}
        fill={i % 2 === 0 ? "#b22234" : "#ffffff"}
      />,
    );
  }
  return (
    <>
      {stripes}
      <rect x="0" y="0" width="11" height={band * 4} fill="#3c3b6e" />
      {star(3.2, 3.2, 1.5, "#ffffff", "s1")}
      {star(7.8, 3.2, 1.5, "#ffffff", "s2")}
      {star(5.5, 6.8, 1.5, "#ffffff", "s3")}
      {star(3.2, 10.4, 1.5, "#ffffff", "s4")}
      {star(7.8, 10.4, 1.5, "#ffffff", "s5")}
    </>
  );
}

function EuFlag() {
  const stars: ReactNode[] = [];
  for (let i = 0; i < 12; i += 1) {
    const angle = (Math.PI / 6) * i - Math.PI / 2;
    stars.push(
      star(12 + 7 * Math.cos(angle), 12 + 7 * Math.sin(angle), 1.5, "#ffcc00", `eu${i}`),
    );
  }
  return (
    <>
      <rect x="0" y="0" width="24" height="24" fill="#003399" />
      {stars}
    </>
  );
}

function TrFlag() {
  return (
    <>
      <rect x="0" y="0" width="24" height="24" fill="#e30a17" />
      {/* Crescent: a white disc with a red one bitten out of it. */}
      <circle cx="10" cy="12" r="5" fill="#ffffff" />
      <circle cx="11.6" cy="12" r="4" fill="#e30a17" />
      {star(17, 12, 3, "#ffffff", "tr")}
    </>
  );
}

function BrFlag() {
  return (
    <>
      <rect x="0" y="0" width="24" height="24" fill="#009b3a" />
      <polygon points="12,2.5 22,12 12,21.5 2,12" fill="#fedf00" />
      <circle cx="12" cy="12" r="4.4" fill="#002776" />
      <path d="M7.9 10.4a9.5 9.5 0 018.3 2.4" stroke="#ffffff" strokeWidth="1.1" fill="none" />
    </>
  );
}

function IdFlag() {
  return (
    <>
      <rect x="0" y="0" width="24" height="12" fill="#ce1126" />
      <rect x="0" y="12" width="24" height="12" fill="#ffffff" />
    </>
  );
}

function CaFlag() {
  // The real 11-point maple leaf turns to mush at 24px; this silhouette survives.
  return (
    <>
      <rect x="0" y="0" width="24" height="24" fill="#ffffff" />
      <rect x="0" y="0" width="6" height="24" fill="#d52b1e" />
      <rect x="18" y="0" width="6" height="24" fill="#d52b1e" />
      <path
        d="M12 4.6l1.5 3.1 2.5-.7-.9 2.6 2.4 1.3-2.4 1.3.9 2.6-2.5-.7-1.5 3.1-1.5-3.1-2.5.7.9-2.6L6.5 11l2.4-1.3-.9-2.6 2.5.7z"
        fill="#d52b1e"
      />
      <rect x="11.4" y="14" width="1.2" height="5" fill="#d52b1e" />
    </>
  );
}

const FLAG_ART: Readonly<Record<FlagId, () => ReactNode>> = {
  us: UsFlag,
  eu: EuFlag,
  tr: TrFlag,
  br: BrFlag,
  id: IdFlag,
  ca: CaFlag,
};

/** Issuer marks outrank the flag: a user knows the coin's own logo before its ticker. */
const BRAND_BY_SYMBOL: Readonly<Record<string, () => ReactNode>> = {
  CADC: () => (
    <>
      <circle cx="12" cy="12" r="12" fill="#8f1d21" />
      <circle cx="12" cy="12" r="8.4" fill="none" stroke="#ffffff" strokeWidth="1.5" />
      {/* The crosshair through the ring, as on the issuer's mark. */}
      <path
        d="M12 1.4v21.2M1.4 12h21.2"
        stroke="#ffffff"
        strokeWidth="1.5"
        strokeLinecap="butt"
      />
      <circle cx="12" cy="12" r="3.1" fill="#8f1d21" />
    </>
  ),
  USDC: () => (
    <>
      <circle cx="12" cy="12" r="12" fill="#2775ca" />
      {/* The dollar: a stroked S with the stem running through it. */}
      <path
        d="M14.3 8.9c-.35-1.05-1.25-1.7-2.5-1.7-1.5 0-2.5.75-2.5 1.9 0 1 .7 1.55 2.2 1.9l1.1.25c1.95.45 2.9 1.3 2.9 2.85 0 1.75-1.4 2.9-3.35 3.05"
        fill="none"
        stroke="#ffffff"
        strokeWidth="1.45"
        strokeLinecap="round"
      />
      <path
        d="M9.6 15.3c.4 1.05 1.35 1.65 2.65 1.65"
        fill="none"
        stroke="#ffffff"
        strokeWidth="1.45"
        strokeLinecap="round"
      />
      <path d="M12 5.6v12.8" stroke="#ffffff" strokeWidth="1.45" strokeLinecap="round" />
    </>
  ),
};

/** The issuer mark for a symbol, or null when there is none. */
export function brandFor(symbol: string): (() => ReactNode) | null {
  return BRAND_BY_SYMBOL[symbol.toUpperCase()] ?? null;
}

/** The flag a symbol should wear, or null when the fallback circle applies. */
export function flagFor(symbol: string): FlagId | null {
  return FLAG_BY_SYMBOL[symbol.toUpperCase()] ?? null;
}

export function TokenIcon({ token, size = 24 }: { token: Token; size?: number }) {
  const id = useId();
  const brand = brandFor(token.symbol);
  const flag = brand === null ? flagFor(token.symbol) : null;
  const twoTone = token.colorB !== undefined;
  const clipId = `${id}-clip`;
  const gradientId = `${id}-grad`;

  return (
    <svg
      className="token-icon"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <defs>
        <clipPath id={clipId}>
          <circle cx="12" cy="12" r="12" />
        </clipPath>
        {flag === null && twoTone && (
          <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="1">
            <stop offset="45%" stopColor={token.color} />
            <stop offset="55%" stopColor={token.colorB} />
          </linearGradient>
        )}
      </defs>

      {brand !== null ? (
        <g clipPath={`url(#${clipId})`}>{brand()}</g>
      ) : flag === null ? (
        <>
          <circle cx="12" cy="12" r="12" fill={twoTone ? `url(#${gradientId})` : token.color} />
          <text
            x="12"
            y="12.5"
            textAnchor="middle"
            dominantBaseline="central"
            fill="#ffffff"
            fontSize={token.glyph.length > 1 ? 9 : 11}
            fontWeight="700"
            fontFamily="system-ui, sans-serif"
          >
            {token.glyph}
          </text>
        </>
      ) : (
        <g clipPath={`url(#${clipId})`}>{FLAG_ART[flag]()}</g>
      )}

      {/* The rim reads as the coin's edge and keeps a white flag (ID, US) from
          dissolving into a light card. */}
      <circle
        cx="12"
        cy="12"
        r="11.25"
        fill="none"
        stroke="rgba(0,0,0,0.18)"
        strokeWidth="1.5"
      />
    </svg>
  );
}
